import {
  Group,
  HalfMoon,
  KeyCommand,
  Language,
  LogOut,
  SunLight,
} from 'iconoir-react';
import { FC, useEffect, useState } from 'react';

import { safeFormattedIntlString } from '@savgroup-front-common/core/formatters';
import { ExternalPortal } from '@savgroup-front-common/core/protons/ExternalPortal/ExternalPortal';
import {
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
} from '@savgroup-front-common/primitive/ui/dropdown-menu';

import { MaintenanceScreen } from '../../../../molecules/AppStatuses';
import {
  useAppComponents,
  useAppIncidents,
  useAppScheduledMaintenances,
} from '../../../../molecules/AppStatuses/hooks';
import { useProfileMenuData } from '../../hooks/useProfileMenuData';
import { SelectSellerSheet } from '../../SelectSeller/SelectSellerSheet/SelectSellerSheet';
import { useShortcutsModal } from '../hooks';
import ShortcutsModal from '../ShortcutsModal';

import HeaderProfileAppStatus from './HeaderProfileAppStatus';
import { HeaderProfileMenuItem } from './HeaderProfileMenuItem/HeaderProfileMenuItem';
import { HeaderProfileMenuLanguageItems } from './HeaderProfileMenuLanguageItems/HeaderProfileMenuLanguageItems';
import { HeaderProfileMenuSubTrigger } from './HeaderProfileMenuSubTrigger/HeaderProfileMenuSubTrigger';
import HeaderProfileUserName from './HeaderProfileUserName';
import useMaintenanceStatus from './hooks/useMaintenanceStatus';
import messages from './messages';

interface HeaderProfileMenuProps {
  onClose: () => void;
}

const HeaderProfileMenu: FC<HeaderProfileMenuProps> = ({ onClose }) => {
  const [isSellerSheetOpen, setIsSellerSheetOpen] = useState(false);

  const {
    handleCloseShortcutsModal,
    isShortcutsModalOpen,
    onOpenShortcutModal,
  } = useShortcutsModal();

  const {
    hasMultipleLanguages,
    hasSellerPicker,
    logout,
    isDarkModeEnabled,
    onToggleDarkMode,
  } = useProfileMenuData();

  const componentName = 'BackOffice';

  useAppIncidents({ componentName });

  const {
    appComponents,
    updateAppComponents,
    isUnderMaintenance,
    removeMaintenanceScreen,
  } = useAppComponents({
    componentName,
  });
  const { nextScheduledMaintenance, appScheduledMaintences } =
    useAppScheduledMaintenances({
      appComponents,
      componentName,
      updateAppComponents,
    });

  useMaintenanceStatus({
    nextScheduledMaintenance,
  });

  useEffect(() => {
    updateAppComponents();
  }, [updateAppComponents]);

  if (isUnderMaintenance) {
    return (
      <ExternalPortal>
        <MaintenanceScreen onClose={() => removeMaintenanceScreen()} />
      </ExternalPortal>
    );
  }

  return (
    <>
      <ShortcutsModal
        isOpen={isShortcutsModalOpen}
        onClose={handleCloseShortcutsModal}
      />
      <SelectSellerSheet
        isOpen={isSellerSheetOpen}
        onOpenChange={setIsSellerSheetOpen}
        onSelected={onClose}
        dataTestId="headerProfileSelectSellerSheet"
      />
      <DropdownMenuContent
        align="end"
        className="w-80"
        data-testid="headerProfileMenu"
      >
        <div className="px-2 py-1.5">
          <HeaderProfileUserName />
        </div>

        <DropdownMenuSeparator />

        <div className="px-2 py-1.5">
          <HeaderProfileAppStatus
            appScheduledMaintences={appScheduledMaintences}
            appComponents={appComponents}
          />
        </div>

        <DropdownMenuSeparator />

        {hasSellerPicker && (
          <HeaderProfileMenuItem
            icon={<Group />}
            dataTestId="headerProfileSelectSeller"
            onSelect={() => setIsSellerSheetOpen(true)}
          >
            {safeFormattedIntlString(messages.selectSeller)}
          </HeaderProfileMenuItem>
        )}

        {hasMultipleLanguages && (
          <DropdownMenuSub>
            <HeaderProfileMenuSubTrigger
              icon={<Language />}
              dataTestId="headerProfileLanguage"
            >
              {safeFormattedIntlString(messages.languageLabel)}
            </HeaderProfileMenuSubTrigger>
            <DropdownMenuSubContent className="min-w-48">
              <HeaderProfileMenuLanguageItems onSelect={onClose} />
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}

        <HeaderProfileMenuItem
          icon={isDarkModeEnabled ? <SunLight /> : <HalfMoon />}
          dataTestId="menu_darkmode"
          onSelect={onToggleDarkMode}
        >
          {isDarkModeEnabled
            ? safeFormattedIntlString(messages.lightmode)
            : safeFormattedIntlString(messages.darkmode)}
        </HeaderProfileMenuItem>

        <HeaderProfileMenuItem
          icon={<KeyCommand />}
          dataTestId="menu_shortcut"
          onSelect={onOpenShortcutModal}
        >
          {safeFormattedIntlString(messages.shortcuts)}
        </HeaderProfileMenuItem>

        <DropdownMenuSeparator />

        <HeaderProfileMenuItem
          icon={<LogOut />}
          variant="destructive"
          dataTestId="logoutButton"
          onSelect={() => logout()}
        >
          {safeFormattedIntlString(messages.logout)}
        </HeaderProfileMenuItem>
      </DropdownMenuContent>
    </>
  );
};

export default HeaderProfileMenu;
